import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const DEFAULT_ROBOTS = `User-agent: *
Allow: /

# Block admin and private areas
Disallow: /admin/
Disallow: /dashboard/
Disallow: /settings/
Disallow: /api/

# Crawl delay
Crawl-delay: 10

# Sitemap location
Sitemap: ${window.location?.origin}/sitemap.xml`;

const RobotsManager = () => {
  const [content, setContent] = useState(DEFAULT_ROBOTS);
  const [savedContent, setSavedContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('editor');
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    loadRobots();
  }, []);

  const loadRobots = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('seo_tools_config')?.select('config_value')?.eq('config_key', 'robots_txt')?.maybeSingle();

      if (data?.config_value) {
        setContent(data?.config_value);
        setSavedContent(data?.config_value);
      } else {
        setSavedContent(DEFAULT_ROBOTS);
      }
    } catch (e) {
      setSavedContent(DEFAULT_ROBOTS);
    }
    setLoading(false);
  };

  const validateRobots = (text) => {
    const errors = [];
    const lines = text?.split('\n');
    let hasUserAgent = false;
    lines?.forEach((line, i) => {
      const trimmed = line?.trim();
      if (trimmed?.startsWith('User-agent:')) hasUserAgent = true;
      if (trimmed && !trimmed?.startsWith('#') && !trimmed?.startsWith('User-agent:') &&
          !trimmed?.startsWith('Allow:') && !trimmed?.startsWith('Disallow:') &&
          !trimmed?.startsWith('Crawl-delay:') && !trimmed?.startsWith('Sitemap:') &&
          !trimmed?.startsWith('Host:') && !trimmed?.startsWith('Request-rate:')) {
        errors?.push(`Line ${i + 1}: Unknown directive "${trimmed?.split(':')?.[0]}"`);
      }
    });
    if (!hasUserAgent) errors?.push('Missing required User-agent directive');
    return errors;
  };

  const handleContentChange = (value) => {
    setContent(value);
    setValidationErrors(validateRobots(value));
    setSaved(false);
  };

  const handleSave = async () => {
    const errors = validateRobots(content);
    if (errors?.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setSaving(true);
    try {
      await supabase?.from('seo_tools_config')?.upsert(
        { config_key: 'robots_txt', config_value: content, updated_at: new Date()?.toISOString() },
        { onConflict: 'config_key' }
      );
      setSavedContent(content);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Save error:', e);
    }
    setSaving(false);
  };

  const handleReset = () => {
    setContent(DEFAULT_ROBOTS);
    setValidationErrors([]);
    setSaved(false);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(content);
  };

  const isDirty = content !== savedContent;

  const presetRules = [
    { label: 'Block all bots', value: 'User-agent: *\nDisallow: /' },
    { label: 'Allow all bots', value: 'User-agent: *\nAllow: /' },
    { label: 'Block GPTBot (ChatGPT)', value: 'User-agent: GPTBot\nDisallow: /' },
    { label: 'Block Google-Extended (Gemini)', value: 'User-agent: Google-Extended\nDisallow: /' },
    { label: 'Block CCBot (Common Crawl)', value: 'User-agent: CCBot\nDisallow: /' },
    { label: 'Block all AI crawlers', value: 'User-agent: GPTBot\nDisallow: /\n\nUser-agent: Google-Extended\nDisallow: /\n\nUser-agent: CCBot\nDisallow: /\n\nUser-agent: anthropic-ai\nDisallow: /\n\nUser-agent: Claude-Web\nDisallow: /' },
  ];

  const appendPreset = (value) => {
    setContent(prev => prev + '\n\n' + value);
    setSaved(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Robots.txt Manager</h2>
            <p className="text-sm text-gray-600">Control crawler access to your website from the admin panel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full hidden md:block">Unsaved changes</span>
          )}
          <Button
            variant="primary"
            iconName={saved ? 'Check' : 'Save'}
            iconPosition="left"
            onClick={handleSave}
            disabled={saving || !isDirty}
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {['editor', 'presets', 'preview']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      {activeTab === 'editor' && (
        <div className="space-y-4">
          {validationErrors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertCircle" size={16} className="text-red-500" />
                <span className="text-sm font-medium text-red-700">Validation Errors</span>
              </div>
              <ul className="space-y-1">
                {validationErrors?.map((err, i) => (
                  <li key={i} className="text-xs text-red-600">• {err}</li>
                ))}
              </ul>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
            </div>
          ) : (
            <div className="relative">
              <div className="flex items-center justify-between bg-gray-800 px-4 py-2 rounded-t-lg">
                <span className="text-xs text-gray-400 font-mono">robots.txt</span>
                <div className="flex gap-2">
                  <button onClick={handleCopy} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                    <Icon name="Copy" size={12} /> Copy
                  </button>
                  <button onClick={handleReset} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                    <Icon name="RotateCcw" size={12} /> Reset
                  </button>
                </div>
              </div>
              <textarea
                value={content}
                onChange={e => handleContentChange(e?.target?.value)}
                rows={16}
                className="w-full px-4 py-3 bg-gray-900 text-green-400 font-mono text-sm border-0 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                spellCheck={false}
              />
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-blue-50 rounded-lg p-3">
            <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0" />
            <span>Changes saved here will be reflected in your live robots.txt file. Use the Presets tab to quickly add common rules.</span>
          </div>
        </div>
      )}
      {activeTab === 'presets' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">Click any preset to append it to your robots.txt</p>
          {presetRules?.map((preset, i) => (
            <div key={i} className="flex items-center justify-between border border-gray-200 rounded-lg p-3 hover:border-orange-300 transition-colors">
              <div>
                <div className="text-sm font-medium text-gray-900">{preset?.label}</div>
                <pre className="text-xs text-gray-500 mt-1 font-mono">{preset?.value}</pre>
              </div>
              <button
                onClick={() => appendPreset(preset?.value)}
                className="ml-4 flex-shrink-0 text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                Append
              </button>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
            <Icon name="Globe" size={16} className="text-gray-500" />
            <span className="text-sm text-gray-600 font-mono">{window.location?.origin}/robots.txt</span>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Live Preview</span>
            </div>
            <pre className="p-4 text-sm text-gray-800 font-mono whitespace-pre-wrap bg-white max-h-80 overflow-y-auto">
              {content}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default RobotsManager;
